import React, { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { TenantProvider, useTenant } from '../contexts/TenantContext';
import Homepage from '../pages/public/Homepage';
import EventsPage from '../pages/public/EventsPage';
import EventDetail from '../pages/public/EventDetail';
import RulesPage from '../pages/public/RulesPage';
import RuleCategoryPage from '../pages/public/RuleCategoryPage';
import RuleDetailPage from '../pages/public/RuleDetailPage';
import SchedulePage from '../pages/schedule/SchedulePage';
import TrainingDetailPage from '../pages/schedule/TrainingDetailPage';
import EventDetailPage from '../pages/schedule/EventDetailPage';
import DynamicPage from '../pages/public/DynamicPage';

// Import new components
import HistoryPage from '../pages/public/HistoryPage';
import SimpleEventsPage from '../pages/public/SimpleEventsPage';
import ContactUsPage from '../pages/public/ContactUsPage';
import OfficeBearersPage from '../pages/public/OfficeBearersPage';

// Lazy load ThrowballLayout as default layout
const ThrowballLayout = lazy(() => import('../components/themes/throwball/ThrowballLayout'));
import SimpleBearersPage from '../pages/public/SimpleBearersPage';
import RegistrationPage from '../pages/public/RegistrationPage';
import InternationalRecognitionPage from '../pages/public/InternationalRecognitionPage';
import TestRoute from '../components/TestRoute';
import ProfessionalLoader from '../components/common/ProfessionalLoader';

// Import all ABFI pages
import AbbreviationPage from '../pages/public/AbbreviationPage';
import AccountsPage from '../pages/public/AccountsPage';
import AffiliatesPage from '../pages/public/AffiliatesPage';
import BaseballLingoPage from '../pages/public/BaseballLingoPage';
import BaseballRulesPage from '../pages/public/BaseballRulesPage';
import BattingOrderPage from '../pages/public/BattingOrderPage';
import CalendarEventsPage from '../pages/public/CalendarEventsPage';
import CertificateVerifyPage from '../pages/public/CertificateVerifyPage';
import CommitteesPage from '../pages/public/CommitteesPage';
import CompleteBearersPage from '../pages/public/CompleteBearersPage';
import FieldingPositionPage from '../pages/public/FieldingPositionPage';
import ForthcomingEventsPage from '../pages/public/ForthcomingEventsPage';
import RTIPage from '../pages/public/RTIPage';
import ScoreKeepingPage from '../pages/public/ScoreKeepingPage';
import SkillsPage from '../pages/public/SkillsPage';
import StaticEventsPage from '../pages/public/StaticEventsPage';
import StaticGalleryPage from '../pages/public/StaticGalleryPage';
import Stats101Page from '../pages/public/Stats101Page';
import UserLoginPage from '../pages/public/UserLoginPage';
import WebsiteInfoPage from '../pages/public/WebsiteInfoPage';
import WhyPlayPage from '../pages/public/WhyPlayPage';

// Import theme components
import { getTheme } from '../components/themes';
import {
  ThrowballAbout,
  ThrowballGoverningBody,
  ThrowballStates,
  ThrowballOfficials,
  ThrowballChampionships,
  ThrowballChampionshipDetail,
  ThrowballLeaderboards,
  ThrowballGallery,
  ThrowballNews,
  ThrowballNewsDetail,
  ThrowballContact,
  ThrowballCommittees,
  ThrowballDownloads,
  ThrowballRTI,
  ThrowballAccounts,
  ThrowballVerifyCertificates,
  ThrowballLogin,
  ThrowballRules,
  LeagueCities,
  LeagueSports,
} from '../components/themes/throwball/pages';

// Theme-aware page wrapper component
const ThemeAwarePage = ({ defaultComponent: DefaultComponent, themeComponents = {} }) => {
  const { tenant, loading } = useTenant();

  if (loading) {
    return <ProfessionalLoader />;
  }

  const themeName = tenant?.theme || 'throwball';
  const theme = getTheme(themeName);

  // Check if there's a theme-specific component for this page
  const ThemeComponent = themeComponents[themeName];

  if (ThemeComponent && theme.Layout) {
    const ThemeLayout = theme.Layout;
    return (
      <Suspense fallback={<ProfessionalLoader />}>
        <ThemeLayout>
          <ThemeComponent />
        </ThemeLayout>
      </Suspense>
    );
  }

  // Fallback to default component with ThrowballLayout
  return DefaultComponent ? (
    <Suspense fallback={<ProfessionalLoader />}>
      <ThrowballLayout>
        <DefaultComponent />
      </ThrowballLayout>
    </Suspense>
  ) : null;
};

// Theme-aware login page (no layout wrapper needed)
const ThemeAwareLoginPage = ({ defaultComponent: DefaultComponent, themeComponents = {} }) => {
  const { tenant, loading } = useTenant();

  if (loading) {
    return <ProfessionalLoader />;
  }

  const themeName = tenant?.theme || 'throwball';

  // Check if there's a theme-specific component for this page
  const ThemeComponent = themeComponents[themeName];

  if (ThemeComponent) {
    return (
      <Suspense fallback={<ProfessionalLoader />}>
        <ThemeComponent />
      </Suspense>
    );
  }

  // Fallback to default component
  return DefaultComponent ? <DefaultComponent /> : null;
};

const PublicRoutes = () => {
  return (
    <TenantProvider>
      <Routes>
        {/* Landing Page without layout */}
        <Route index element={<Homepage />} />

        {/* Direct routes without nested layout */}
        <Route path="test" element={<TestRoute />} />
        {/* Office Bearers - Old design like abfi.in */}
        <Route path="bearer" element={<CompleteBearersPage />} />
        <Route path="executive-committee" element={<SimpleBearersPage />} />

        {/* Theme-aware pages */}
        <Route path="about" element={
          <ThemeAwarePage
            defaultComponent={HistoryPage}
            themeComponents={{ throwball: ThrowballAbout }}
          />
        } />
        {/* League pages — distinct routes rather than relabelled federation
            pages, so a league tenant gets its own content and URLs. */}
        <Route path="cities" element={
          <ThemeAwarePage
            defaultComponent={LeagueCities}
            themeComponents={{ throwball: LeagueCities }}
          />
        } />
        <Route path="sports" element={
          <ThemeAwarePage
            defaultComponent={LeagueSports}
            themeComponents={{ throwball: LeagueSports }}
          />
        } />
        <Route path="governing-body" element={
          <ThemeAwarePage
            defaultComponent={CompleteBearersPage}
            themeComponents={{ throwball: ThrowballGoverningBody }}
          />
        } />
        <Route path="states" element={
          <ThemeAwarePage
            defaultComponent={AffiliatesPage}
            themeComponents={{ throwball: ThrowballStates }}
          />
        } />
        <Route path="officials" element={
          <ThemeAwarePage
            defaultComponent={CompleteBearersPage}
            themeComponents={{ throwball: ThrowballOfficials }}
          />
        } />
        <Route path="championships" element={
          <ThemeAwarePage
            defaultComponent={StaticEventsPage}
            themeComponents={{ throwball: ThrowballChampionships }}
          />
        } />
        <Route path="leaderboards" element={
          <ThemeAwarePage
            defaultComponent={StaticEventsPage}
            themeComponents={{ throwball: ThrowballLeaderboards }}
          />
        } />
        {/* "View Details" on the homepage has always linked here. */}
        <Route path="championships/:id" element={
          <ThemeAwarePage
            defaultComponent={StaticEventsPage}
            themeComponents={{ throwball: ThrowballChampionshipDetail }}
          />
        } />
        <Route path="photo-gallery" element={
          <ThemeAwarePage
            defaultComponent={StaticGalleryPage}
            themeComponents={{ throwball: ThrowballGallery }}
          />
        } />
        <Route path="news" element={
          <ThemeAwarePage
            defaultComponent={StaticEventsPage}
            themeComponents={{ throwball: ThrowballNews }}
          />
        } />
        <Route path="news/:id" element={
          <ThemeAwarePage
            defaultComponent={StaticEventsPage}
            themeComponents={{ throwball: ThrowballNewsDetail }}
          />
        } />
        <Route path="contact" element={
          <ThemeAwarePage
            defaultComponent={ContactUsPage}
            themeComponents={{ throwball: ThrowballContact }}
          />
        } />
        <Route path="committees" element={
          <ThemeAwarePage
            defaultComponent={CommitteesPage}
            themeComponents={{ throwball: ThrowballCommittees }}
          />
        } />
        <Route path="downloads" element={
          <ThemeAwarePage
            defaultComponent={WebsiteInfoPage}
            themeComponents={{ throwball: ThrowballDownloads }}
          />
        } />
        <Route path="rti" element={
          <ThemeAwarePage
            defaultComponent={RTIPage}
            themeComponents={{ throwball: ThrowballRTI }}
          />
        } />
        <Route path="accounts" element={
          <ThemeAwarePage
            defaultComponent={AccountsPage}
            themeComponents={{ throwball: ThrowballAccounts }}
          />
        } />
        <Route path="throwball-rules" element={
          <ThemeAwarePage
            defaultComponent={BaseballRulesPage}
            themeComponents={{ throwball: ThrowballRules }}
          />
        } />
        <Route path="verify-certificates" element={
          <ThemeAwarePage
            defaultComponent={CertificateVerifyPage}
            themeComponents={{ throwball: ThrowballVerifyCertificates }}
          />
        } />

        {/* All other pages with layout */}
        {/* Events - Old design like abfi.in */}
        <Route path="events" element={<StaticEventsPage />} />

        {/* Event Detail - Keep for individual event pages */}
        <Route path="events/:id" element={<EventDetail />} />

        {/* Baseball Rules - Old design like abfi.in */}
        <Route path="rules" element={<BaseballRulesPage />} />

        {/* New Rules System - Keep for future */}
        {/* <Route path="rules" element={<Suspense fallback={<ProfessionalLoader />}><ThrowballLayout /></Suspense>}>
          <Route index element={<RulesPage />} />
          <Route path="category/:categorySlug" element={<RuleCategoryPage />} />
          <Route path=":ruleSlug" element={<RuleDetailPage />} />
        </Route> */}

        <Route path="schedule" element={<Suspense fallback={<ProfessionalLoader />}><ThrowballLayout /></Suspense>}>
          <Route index element={<SchedulePage />} />
          <Route path="training/:id" element={<TrainingDetailPage />} />
          <Route path="event/:id" element={<EventDetailPage />} />
        </Route>

        <Route path="calendar" element={<Suspense fallback={<ProfessionalLoader />}><ThrowballLayout /></Suspense>}>
          <Route index element={<SchedulePage />} />
        </Route>

        {/* Individual pages without layout wrapper (they have their own) */}
        <Route path="history" element={<HistoryPage />} />
        <Route path="affiliates" element={<AffiliatesPage />} />
        <Route path="registration" element={<RegistrationPage />} />
        <Route path="international-recognition" element={<InternationalRecognitionPage />} />
        <Route path="certificates" element={<CertificateVerifyPage />} />
        <Route path="certificate-verify" element={<CertificateVerifyPage />} />
        <Route path="website-info" element={<WebsiteInfoPage />} />
        <Route path="user-login" element={
          <ThemeAwareLoginPage
            defaultComponent={UserLoginPage}
            themeComponents={{ throwball: ThrowballLogin }}
          />
        } />
        <Route path="abbreviation" element={<AbbreviationPage />} />
        <Route path="baseball-lingo" element={<BaseballLingoPage />} />
        <Route path="batting-order" element={<BattingOrderPage />} />
        <Route path="fielding-position" element={<FieldingPositionPage />} />
        <Route path="score-keeping" element={<ScoreKeepingPage />} />
        <Route path="skills" element={<SkillsPage />} />
        <Route path="stats" element={<Stats101Page />} />
        <Route path="why-play" element={<WhyPlayPage />} />
        <Route path="forthcoming-events" element={<ForthcomingEventsPage />} />
        <Route path="calendar-events" element={<CalendarEventsPage />} />

        {/* Dynamic page route (must be last) */}
        <Route path=":slug" element={<Suspense fallback={<ProfessionalLoader />}><ThrowballLayout /></Suspense>}>
          <Route index element={<DynamicPage />} />
        </Route>
      </Routes>
    </TenantProvider>
  );
};

export default PublicRoutes
